/**
 * Notifications log (supervisor/admin) — every update the system has sent, to
 * whom, on which channel, and whether it landed. Defaults to email (the
 * external sends) with filters for channel / status / event / recipient.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  BellOff,
  Mail,
  MessagesSquare,
  Monitor,
  RefreshCw,
  Search,
  Smartphone,
  Users,
} from 'lucide-react';
import {
  NotificationChannelSchema,
  NotificationEventSchema,
  NotificationStatusSchema,
  type NotificationDeliveryMode,
  type NotificationSettings,
} from '@vari-crew/schemas';
import { useNotificationsLog } from '@/api/notifications-log';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from '@/api/notification-settings';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { cn } from '@/lib/cn';

const PAGE_SIZE = 50;

const EVENT_LABEL: Record<string, string> = {
  project_scheduled: 'Project scheduled',
  project_approval_requested: 'New project request',
  project_approved: 'Project approved',
  crew_assignment_changed: 'Crew assignment updated',
  en_route: 'Crew on the way',
  on_site: 'Crew on site',
  punch_walkthrough_ready: 'Walkthrough ready',
  complete_recap_available: 'Recap available',
  blocked_needs_attention: 'Blocked',
  note_added: 'Note added',
};

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  teams: 'Teams',
  in_app: 'In-app',
  sms: 'SMS',
  push: 'Push',
};

const CHANNEL_ICON: Record<string, typeof Mail> = {
  email: Mail,
  teams: MessagesSquare,
  in_app: Bell,
  sms: Smartphone,
  push: Monitor,
};

const STATUS_STYLE = {
  sent: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  delivered: 'text-sky-700 bg-sky-50 border-sky-200',
  queued: 'text-amber-700 bg-amber-50 border-amber-200',
  failed: 'text-red-700 bg-red-50 border-red-200',
  suppressed: 'text-gray-500 bg-gray-100 border-gray-200',
};

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Select({
  value,
  onChange,
  children,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  label: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-ink-faint">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm px-2 py-1.5 border border-line rounded bg-bg min-w-[8rem]"
      >
        {children}
      </select>
    </label>
  );
}

function StatChip({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={cn('px-3 py-2 rounded-lg border flex flex-col min-w-[5.5rem]', tone)}>
      <span className="text-lg font-semibold leading-none">{value}</span>
      <span className="text-[11px] mt-1 opacity-80">{label}</span>
    </div>
  );
}

const DELIVERY_MODES: {
  value: NotificationDeliveryMode;
  label: string;
  desc: string;
  icon: typeof Mail;
}[] = [
  {
    value: 'normal',
    label: 'Real recipients',
    desc: 'Send to the actual people each event is for.',
    icon: Mail,
  },
  {
    value: 'divert_to_admins',
    label: 'Divert to admins',
    desc: 'Email goes to admins instead; Teams/SMS held. Safe for testing.',
    icon: Users,
  },
  {
    value: 'disabled',
    label: 'Off',
    desc: 'No external sends. The in-app bell still works.',
    icon: BellOff,
  },
];

const MODE_LABEL: Record<NotificationDeliveryMode, string> = {
  normal: 'Real recipients',
  divert_to_admins: 'Divert to admins',
  disabled: 'Off',
};

/** Admin-only control: where notification emails actually go. */
function DeliveryControls() {
  const { data, isLoading } = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  if (isLoading || !data) return null;

  const choose = (mode: NotificationDeliveryMode) => {
    if (mode === data.mode || update.isPending) return;
    update.mutate(
      { mode },
      {
        onSuccess: () => toast.success(`Delivery set to "${MODE_LABEL[mode]}"`),
        onError: () => toast.error('Could not update delivery mode'),
      },
    );
  };

  // Empty stored selection means "all admins"; expand it so the checkboxes show
  // every admin checked, then edits store the explicit subset.
  const selected = data.divert_recipient_ids.length
    ? data.divert_recipient_ids
    : data.admins.map((a) => a.id);
  const toggleRecipient = (id: string) => {
    if (update.isPending) return;
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    if (next.length === 0) {
      toast.error('Pick at least one admin');
      return;
    }
    update.mutate(
      { divert_recipient_ids: next },
      { onError: () => toast.error('Could not update recipients') },
    );
  };

  const warn = data.mode !== 'normal';
  return (
    <div
      className={cn(
        'border rounded-lg p-4',
        warn ? 'border-amber-300 bg-amber-50/40' : 'border-line',
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-medium">Email delivery</h2>
        {warn && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            real recipients aren't getting emails
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {DELIVERY_MODES.map((m) => {
          const active = data.mode === m.value;
          return (
            <button
              key={m.value}
              onClick={() => choose(m.value)}
              disabled={update.isPending}
              className={cn(
                'text-left border rounded-lg p-3 transition disabled:opacity-60',
                active
                  ? 'border-ink bg-fill-soft ring-1 ring-ink/10'
                  : 'border-line hover:bg-fill-soft',
              )}
            >
              <div className="flex items-center gap-2 font-medium text-sm">
                <m.icon className="w-4 h-4" />
                {m.label}
                {active && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-ink-faint">
                    Current
                  </span>
                )}
              </div>
              <p className="text-xs text-ink-dim mt-1">{m.desc}</p>
              {m.value === 'divert_to_admins' && data.divert_emails.length > 0 && (
                <p
                  className="text-[11px] text-ink-faint mt-1.5 truncate"
                  title={data.divert_emails.join(', ')}
                >
                  → {data.divert_emails.join(', ')}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {data.mode === 'divert_to_admins' && (
        <p className="text-xs text-ink-dim mt-3 bg-fill-soft border border-line rounded p-2.5 leading-relaxed">
          While diverting, every recipient&rsquo;s email is rerouted to the admins below —{' '}
          <span className="font-medium">except people switched to &ldquo;Live&rdquo;</span> in{' '}
          Users &amp; Access, who get their real emails. In the log, a{' '}
          <span className="text-amber-700 font-medium">diverted</span> row means that recipient
          isn&rsquo;t Live yet (it went to the admins); a plain <span className="font-medium">Sent</span>{' '}
          row means it reached the real person. Flip people to Live one at a time to roll out for real.
        </p>
      )}

      <div className="mt-3 border-t border-line pt-3">
        <div className="text-sm font-medium">Divert recipients</div>
        <p className="text-xs text-ink-dim mb-2">
          Which admins receive diverted email (used by &ldquo;Divert to admins&rdquo;).
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {data.admins.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(a.id)}
                disabled={update.isPending}
                onChange={() => toggleRecipient(a.id)}
                className="accent-ink"
              />
              <span>{a.name}</span>
              <span className="text-ink-faint text-xs">{a.email}</span>
            </label>
          ))}
        </div>
      </div>

      {!data.email_armed && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
          Email isn't armed in this environment yet — set{' '}
          <code className="font-mono">SENDGRID_API_KEY</code> +{' '}
          <code className="font-mono">NOTIFICATIONS_STUB_MODE=false</code> on the API app to enable
          real sends. Until then everything is logged, not sent.
        </p>
      )}
    </div>
  );
}

/** Read-only warning for supervisors when sends aren't going to real recipients. */
function DeliveryBanner({ settings }: { settings: NotificationSettings }) {
  if (settings.mode === 'normal') return null;
  const diverted = settings.mode === 'divert_to_admins';
  return (
    <div className="flex items-start gap-2 text-sm border rounded-lg p-3 border-amber-200 bg-amber-50 text-amber-800">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        {diverted ? (
          <>
            <b>Email is diverted to admins.</b> Real recipients aren't receiving notification emails
            right now; Teams/SMS are held. An admin can change this.
          </>
        ) : (
          <>
            <b>External notifications are off.</b> No email/Teams/SMS is being sent — the in-app feed
            still works. An admin can change this.
          </>
        )}
      </div>
    </div>
  );
}

/** A qualifier under the status badge for the surprising delivery cases — a
 *  stub (logged, not really sent) or a diverted send (went to the admins). A
 *  plain "Sent" otherwise means it genuinely went out. */
function DeliveryNote({
  delivery,
  deliveredTo,
}: {
  delivery: string;
  deliveredTo: string | null;
}) {
  if (delivery === 'stub') {
    return (
      <span
        className="text-[10px] text-ink-faint"
        title="Logged in stub mode — not actually sent"
      >
        logged · not sent
      </span>
    );
  }
  if (delivery === 'diverted') {
    return (
      <span
        className="text-[10px] text-amber-700"
        title={deliveredTo ? `Diverted — actually sent to ${deliveredTo}` : 'Diverted to admins'}
      >
        diverted
      </span>
    );
  }
  return null;
}

export function NotificationsLog() {
  const { data: me } = useCurrentUser();
  const isAdmin = me?.role === 'admin';
  const { data: settings } = useNotificationSettings();
  const [channel, setChannel] = useState('email');
  const [status, setStatus] = useState('');
  const [event, setEvent] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  // Debounce the recipient search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change resets to the first page.
  useEffect(() => setPage(0), [channel, status, event, debouncedSearch]);

  const { data, isLoading, isFetching, isError, refetch } = useNotificationsLog({
    channel: channel || undefined,
    status: status || undefined,
    event: event || undefined,
    q: debouncedSearch || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const summary = data?.summary;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE + rows.length);
  const hasMore = page * PAGE_SIZE + rows.length < total;

  const eventOptions = useMemo(() => NotificationEventSchema.options, []);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="text-xl font-semibold">Notifications log</h1>
          <p className="text-sm text-ink-dim mt-0.5">
            Every update the system has sent — and to whom. Defaults to email; switch the channel
            to see Teams and in-app.
          </p>
        </div>
        <button
          onClick={() => void refetch()}
          className="flex items-center gap-1.5 text-sm px-2.5 py-1.5 border border-line rounded hover:bg-fill-soft shrink-0"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {isAdmin ? (
        <div className="mt-4">
          <DeliveryControls />
        </div>
      ) : (
        settings &&
        settings.mode !== 'normal' && (
          <div className="mt-4">
            <DeliveryBanner settings={settings} />
          </div>
        )
      )}

      {/* Summary chips reflect the channel/event/recipient filter (all statuses). */}
      {summary && (
        <div className="flex flex-wrap gap-2 mt-4">
          <StatChip label="Sent" value={summary.sent} tone={STATUS_STYLE.sent} />
          <StatChip label="Delivered" value={summary.delivered} tone={STATUS_STYLE.delivered} />
          <StatChip label="Queued" value={summary.queued} tone={STATUS_STYLE.queued} />
          <StatChip
            label="Failed"
            value={summary.failed}
            tone={summary.failed > 0 ? STATUS_STYLE.failed : 'text-ink-faint bg-fill border-line'}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mt-5">
        <Select value={channel} onChange={setChannel} label="Channel">
          <option value="">All channels</option>
          {NotificationChannelSchema.options.map((c) => (
            <option key={c} value={c}>
              {CHANNEL_LABEL[c] ?? c}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={setStatus} label="Status">
          <option value="">Any status</option>
          {NotificationStatusSchema.options.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </Select>
        <Select value={event} onChange={setEvent} label="Event">
          <option value="">All events</option>
          {eventOptions.map((e) => (
            <option key={e} value={e}>
              {EVENT_LABEL[e] ?? e}
            </option>
          ))}
        </Select>
        <label className="flex flex-col gap-1 flex-1 min-w-[12rem]">
          <span className="text-[10px] uppercase tracking-wide text-ink-faint">Recipient</span>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or email…"
              className="w-full text-sm pl-8 pr-2 py-1.5 border border-line rounded bg-bg"
            />
          </div>
        </label>
      </div>

      {/* Table (desktop) / cards (mobile) */}
      <div className="mt-4 border border-line rounded-lg overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-faint bg-fill-soft border-b border-line">
                <th className="font-medium px-3 py-2 whitespace-nowrap">When</th>
                <th className="font-medium px-3 py-2">Event</th>
                <th className="font-medium px-3 py-2">Channel</th>
                <th className="font-medium px-3 py-2">Recipient</th>
                <th className="font-medium px-3 py-2 hidden md:table-cell">Project</th>
                <th className="font-medium px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const Icon = CHANNEL_ICON[r.channel] ?? Mail;
                return (
                  <tr key={r.id} className="border-b border-line last:border-0 align-top">
                    <td className="px-3 py-2.5 whitespace-nowrap text-ink-dim">
                      {fmtTime(r.sent_at ?? r.queued_at)}
                    </td>
                    <td className="px-3 py-2.5 max-w-[18rem]">
                      <div className="font-medium">{EVENT_LABEL[r.event] ?? r.event}</div>
                      {r.subject && (
                        <div className="text-ink-faint truncate" title={r.subject}>
                          {r.subject}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-ink-dim whitespace-nowrap">
                        <Icon className="w-3.5 h-3.5" />
                        {CHANNEL_LABEL[r.channel] ?? r.channel}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 max-w-[16rem]">
                      <div className="truncate">{r.recipient_name ?? '—'}</div>
                      {r.recipient_email && (
                        <div className="text-ink-faint truncate" title={r.recipient_email}>
                          {r.recipient_email}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell max-w-[14rem]">
                      {r.project_id ? (
                        <Link
                          to={`/projects/${r.project_id}`}
                          className="text-ink-dim hover:text-ink underline-offset-2 hover:underline truncate block"
                          title={r.project_title ?? undefined}
                        >
                          {r.project_title ?? 'Project'}
                        </Link>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={cn(
                          'inline-block px-2 py-0.5 rounded-full border text-xs font-medium capitalize',
                          STATUS_STYLE[r.status as keyof typeof STATUS_STYLE] ?? STATUS_STYLE.suppressed,
                        )}
                      >
                        {r.status}
                      </span>
                      {(r.delivery === 'stub' || r.delivery === 'diverted') && (
                        <div className="mt-0.5">
                          <DeliveryNote delivery={r.delivery} deliveredTo={r.delivered_to} />
                        </div>
                      )}
                      {r.status === 'failed' && r.error && (
                        <div className="text-red-600 text-xs mt-1 max-w-[12rem] truncate" title={r.error}>
                          {r.error}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile: one card per notification — keeps Status + the delivery note
            visible without the table scrolling sideways. */}
        <ul className="md:hidden divide-y divide-line">
          {rows.map((r) => {
            const Icon = CHANNEL_ICON[r.channel] ?? Mail;
            return (
              <li key={r.id} className="px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{EVENT_LABEL[r.event] ?? r.event}</div>
                    {r.subject && (
                      <div className="text-xs text-ink-faint truncate" title={r.subject}>
                        {r.subject}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <span
                      className={cn(
                        'inline-block px-2 py-0.5 rounded-full border text-xs font-medium capitalize',
                        STATUS_STYLE[r.status as keyof typeof STATUS_STYLE] ?? STATUS_STYLE.suppressed,
                      )}
                    >
                      {r.status}
                    </span>
                    {(r.delivery === 'stub' || r.delivery === 'diverted') && (
                      <div className="mt-0.5">
                        <DeliveryNote delivery={r.delivery} deliveredTo={r.delivered_to} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-ink-dim">
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <Icon className="w-3.5 h-3.5" />
                    {CHANNEL_LABEL[r.channel] ?? r.channel}
                  </span>
                  <span className="text-ink-faint shrink-0">·</span>
                  <span className="truncate" title={r.recipient_email ?? undefined}>
                    {r.recipient_name ?? r.recipient_email ?? '—'}
                  </span>
                  <span className="ml-auto shrink-0 whitespace-nowrap text-ink-faint">
                    {fmtTime(r.sent_at ?? r.queued_at)}
                  </span>
                </div>
                {r.project_id && (
                  <Link
                    to={`/projects/${r.project_id}`}
                    className="mt-1 block text-xs text-ink-dim hover:text-ink hover:underline truncate"
                    title={r.project_title ?? undefined}
                  >
                    {r.project_title ?? 'Project'}
                  </Link>
                )}
                {r.status === 'failed' && r.error && (
                  <div className="text-red-600 text-xs mt-1 truncate" title={r.error}>
                    {r.error}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {/* States */}
        {isLoading && (
          <div className="px-3 py-10 text-center text-sm text-ink-dim">Loading…</div>
        )}
        {!isLoading && isError && (
          <div className="px-3 py-10 text-center text-sm text-red-600">
            Couldn't load the log. <button onClick={() => void refetch()} className="underline">Retry</button>
          </div>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <div className="px-3 py-10 text-center text-sm text-ink-dim">
            No notifications match these filters.
          </div>
        )}
      </div>

      {/* Pager */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-3 text-sm text-ink-dim">
          <span>
            Showing {from}–{to} of {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 border border-line rounded disabled:opacity-40 hover:bg-fill-soft"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="px-3 py-1.5 border border-line rounded disabled:opacity-40 hover:bg-fill-soft"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
